import { classOf, DEFAULT_CLASS, MODE_NAMES, PLAYER_LIMITS, playersText, type ClassId, type Mode } from "@eldrun/content";
import { CLOSE_KICKED, CLOSE_SERVER_SHUTDOWN, CLOSE_STAFF_KICKED, ENTRY_REFUSALS, type MatchStateType } from "@eldrun/protocol";
import { playerColor } from "../../render/style/roles.ts";

export interface LobbyPlayerView {
  sessionId: string;
  name: string;
  color: string;
  ready: boolean;
  connected: boolean;
  isOwner: boolean;
  isMe: boolean;
  /** Número do jogador (1 a 4) e a classe (F64), que dá a cor (F85). */
  n: number;
  classId: ClassId;
  className: string;
}

export interface LobbyView {
  code: string;
  isPrivate: boolean;
  mode: string;
  difficulty: string;
  seedCode: string;
  /** Regra de PvP mostrada a todos ("Todos contra todos" na Treta). */
  pvp: string;
  friendlyFire: boolean;
  amOwner: boolean;
  meReady: boolean;
  players: LobbyPlayerView[];
  countdown: number;
  canStart: boolean;
  /** Motivo de não poder começar (mesmas regras do servidor), para o botão do dono. */
  blocker: string | undefined;
  /** Minha classe (F64) e o meu número (1 a 4). */
  myClass: ClassId;
  myN: number;
}

/**
 * Projeção do estado da sala para a tela de lobby. As regras de início espelham as do servidor
 * só para orientar o jogador; quem decide é sempre o servidor.
 */
export function lobbyView(state: MatchStateType, me: string): LobbyView {
  const players: LobbyPlayerView[] = [];
  state.players.forEach((p) => {
    players.push({
      sessionId: p.sessionId,
      name: p.name,
      color: playerColor(p),
      ready: p.ready || p.sessionId === state.ownerId,
      connected: p.connected,
      isOwner: p.sessionId === state.ownerId,
      isMe: p.sessionId === me,
      n: p.slot + 1,
      classId: classOf(p.classId).id,
      className: classOf(p.classId).name,
    });
  });
  players.sort((a, b) => Number(b.isOwner) - Number(a.isOwner) || a.name.localeCompare(b.name, "pt-BR"));
  const connected = players.filter((p) => p.connected);
  let blocker: string | undefined;
  // Mínimo por modo (F54): a Irmandade começa com 1; a Arena espera um rival.
  const min = PLAYER_LIMITS[state.mode as Mode]?.min ?? 1;
  if (connected.length < min) blocker = `Esperando mais ${playersText(min - connected.length)} (a ${MODE_NAMES[state.mode as Mode]} precisa de pelo menos ${playersText(min)}).`;
  else if (connected.some((p) => !p.ready)) blocker = "Esperando todos marcarem pronto.";
  const amOwner = state.ownerId === me;
  return {
    code: state.code,
    isPrivate: state.isPrivate,
    mode: state.mode,
    difficulty: state.difficulty,
    seedCode: state.seedCode,
    pvp: pvpLabel(state.mode, state.friendlyFire),
    friendlyFire: state.friendlyFire,
    amOwner,
    meReady: players.find((p) => p.isMe)?.ready ?? false,
    players,
    countdown: state.countdown,
    canStart: amOwner && blocker === undefined && state.countdown === 0,
    blocker,
    myClass: players.find((p) => p.isMe)?.classId ?? DEFAULT_CLASS,
    myN: players.find((p) => p.isMe)?.n ?? 1,
  };
}

/** Fogo amigo: opcional no Mutirão, sempre ligado na Treta (todos contra todos). */
export function pvpLabel(mode: string, friendlyFire: boolean): string {
  if (mode === "treta") return "todos contra todos";
  return friendlyFire ? "fogo amigo ligado" : "sem fogo amigo";
}

/** Mensagem amigável para o motivo de saída da sala. */
export function leaveMessage(code: number): string {
  if (code === CLOSE_KICKED) return "O dono da sala removeu você da partida.";
  if (code === CLOSE_STAFF_KICKED) return ENTRY_REFUSALS["expulso-equipe"];
  if (code === CLOSE_SERVER_SHUTDOWN) return "O servidor de partidas foi reiniciado. Entre de novo em instantes.";
  if (code === 1000) return "";
  if (code >= 4000) return "A conexão com a sala foi encerrada pelo servidor.";
  return "A conexão com o servidor caiu.";
}
